import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from './api';
import { toast } from 'sonner';
import { Download, FileText, Calendar, User, Building2, ChevronRight, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Forms() {
  const [employees, setEmployees] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('hrms_user') || '{}');
  const isAdmin = ['admin', 'hr_manager', 'payroll_officer'].includes(user.role);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [empRes, payslipsRes] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/payslips`)
      ]);
      setEmployees(empRes.data);
      setPayslips(payslipsRes.data);
      
      // Auto-select current employee if not admin
      if (!isAdmin) {
        const currentEmp = empRes.data.find(emp => emp.email === user.email);
        if (currentEmp) {
          setSelectedEmployee(currentEmp.employee_id);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  };

  const handleDownloadForm = async (formType) => {
    if (!selectedEmployee) {
      toast.error('Please select an employee');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/forms/download/${formType}?employee_id=${selectedEmployee}&year=${selectedYear}`,
        { responseType: 'blob' }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${formType}_${selectedEmployee}_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`${formType} downloaded successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to download ${formType}`);
    } finally {
      setLoading(false);
    }
  };

  const forms = [
    {
      code: 'EA',
      title: 'EA Form (Borang EA)',
      description: 'Annual statement of remuneration for employees. Required for income tax filing.',
      icon: FileText,
      color: 'from-blue-500 to-cyan-500',
      required: 'Annual',
      authority: 'LHDN (Inland Revenue Board)'
    },
    {
      code: 'FORM_E',
      title: 'Form E',
      description: 'EPF member contribution statement. Shows employee and employer EPF contributions.',
      icon: Building2,
      color: 'from-emerald-500 to-teal-500',
      required: 'Annual',
      authority: 'EPF (Employees Provident Fund)'
    },
    {
      code: 'PCB2_II',
      title: 'PCB2(II)',
      description: 'Monthly Tax Deduction (MTD) statement. Summary of PCB deductions for the year.',
      icon: Calendar,
      color: 'from-purple-500 to-pink-500',
      required: 'Monthly/Annual',
      authority: 'LHDN'
    },
    {
      code: 'CP21',
      title: 'CP21',
      description: 'Tax relief declaration form. Used to claim personal reliefs and tax deductions.',
      icon: FileText,
      color: 'from-orange-500 to-red-500',
      required: 'Annual',
      authority: 'LHDN'
    },
    {
      code: 'CP22',
      title: 'CP22',
      description: 'Tax clearance form for employees leaving Malaysia. Required before departure.',
      icon: User,
      color: 'from-indigo-500 to-purple-500',
      required: 'Upon Leaving',
      authority: 'LHDN'
    },
    {
      code: 'CP22A',
      title: 'CP22A',
      description: 'Tax clearance for expatriates/non-residents leaving Malaysia permanently.',
      icon: User,
      color: 'from-pink-500 to-rose-500',
      required: 'Upon Leaving',
      authority: 'LHDN'
    },
    {
      code: 'CP22B',
      title: 'CP22B',
      description: 'Tax clearance for deceased employees. Required for estate settlement.',
      icon: User,
      color: 'from-slate-500 to-slate-700',
      required: 'Upon Death',
      authority: 'LHDN'
    }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Forms & Documents</h2>
        <p className="text-slate-600 mt-1">Download statutory forms and tax documents</p>
      </div>

      {/* Selection Controls */}
      <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Select Employee
            </label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              disabled={!isAdmin}
              data-testid="employee-select"
              className="w-full h-11 rounded-xl border border-slate-300 px-4 bg-white disabled:bg-slate-50"
            >
              <option value="">Choose employee...</option>
              {employees.map(emp => (
                <option key={emp.employee_id} value={emp.employee_id}>
                  {emp.full_name} ({emp.employee_id})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Tax Year
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              data-testid="year-select"
              className="w-full h-11 rounded-xl border border-slate-300 px-4 bg-white"
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
        
        {!selectedEmployee && (
          <div className="mt-4 flex items-start space-x-2 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              Please select an employee to download forms. Forms will be generated with employee's payroll data.
            </p>
          </div>
        )}
      </div>

      {/* Forms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {forms.map((form, idx) => {
          const Icon = form.icon;
          return (
            <div
              key={idx}
              data-testid={`form-card-${form.code}`}
              className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${form.color} flex items-center justify-center`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="text-right">
                  <span className="text-xs font-medium text-slate-500">{form.required}</span>
                </div>
              </div>

              <h3 className="text-lg font-bold text-slate-900 mb-2">{form.title}</h3>
              <p className="text-sm text-slate-600 mb-4 min-h-[60px]">{form.description}</p>
              
              <div className="flex items-center space-x-2 text-xs text-slate-500 mb-4">
                <Building2 className="h-3 w-3" />
                <span>{form.authority}</span>
              </div>

              <Button
                onClick={() => handleDownloadForm(form.code)}
                disabled={!selectedEmployee || loading}
                data-testid={`download-${form.code}`}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </div>
          );
        })}
      </div>

      {/* Info Section */}
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-100 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center">
          <AlertCircle className="h-5 w-5 text-blue-600 mr-2" />
          Important Information
        </h3>
        <div className="space-y-2 text-sm text-slate-700">
          <div className="flex items-start space-x-2">
            <ChevronRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p><strong>EA Form:</strong> Must be provided to employees by February 28th each year</p>
          </div>
          <div className="flex items-start space-x-2">
            <ChevronRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p><strong>Form E:</strong> EPF statement showing total contributions for the year</p>
          </div>
          <div className="flex items-start space-x-2">
            <ChevronRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p><strong>CP22 Forms:</strong> Required for tax clearance when leaving Malaysia</p>
          </div>
          <div className="flex items-start space-x-2">
            <ChevronRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p><strong>Generated Forms:</strong> All forms are auto-generated from your payroll data</p>
          </div>
        </div>
      </div>
    </div>
  );
}